import { getSessionData } from "@repo/shared-utils/common-utils";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { getOrDefaultValue } from "@repo/shared-utils/insert-params";
import { blseInsert } from "@repo/connection-manager/services";
import type { IOffers } from "@repo/connection-manager/types";
import { BLSE_PROD_NAME, V5 } from "@repo/connection-manager/constants";
import { getUserDetails } from "../services/demogApi";
import { getVersionedCallbackUrl, redirectToWebform } from "@repo/shared-utils/common-utils";
import type { SSORedirectHandler } from "../types/SSO.types";

interface ISetFormData {
  resume: boolean;
  custType: string;
  applicationID?: string;
  product?: string;
  prodCode?: string;
  blseResData?: { id: string };
}

const setFormData = ({
  custType,
  applicationID,
  product,
  prodCode,
  blseResData,
  resume,
}: ISetFormData) => {
  return {
    resume: blseResData?.id || applicationID ? resume : false,
    custType,
    product,
    applicationID: blseResData?.id ?? applicationID ?? "",
    prodCode: prodCode ?? "",
  } as const;
};

const getJourneyName = (offerProduct: string, loanAmt: number, offerAmt: number): string => {
  if (loanAmt > offerAmt) {
    return "BLSE_NTB";
  }
  const journeyMap: Record<string, string> = {
    BLSE: "BLSE_OFFER",
    MBLS: "BLSE_OFFER",
    PLSE: "PLSE_OFFER",
  };
  return journeyMap[offerProduct] ?? "BLSE_NTB";
};

const redirectionInAppWebform = (appId: string, productName: string) => {
  const inAppRedirectionPrompt = `inAppDeepLink://bajajsuperapp://dyninApp/${productName}/1/[{"encryptionKey":"","payload":"${encodeURIComponent(
    JSON.stringify({ appId, module: productName })
  )}","module":"test","operation":"custRelationshipDetails"}]/${encodeURIComponent(
    JSON.stringify({ appId, module: productName })
  )}`;
  const win = window as Window & {
    tarsier?: { invokePrompt: (prompt: string, force?: boolean) => unknown };
  };
  win.tarsier?.invokePrompt(inAppRedirectionPrompt, true);
};

export const blseHandler: SSORedirectHandler = async (data) => {
  const { custType, mobileNo } = await getSessionData();
  const productName = (data.data?.productName ?? "BLSE") as "BLSE" | "BLSEBT";
  const product = data.mapping?.ssoProduct?.[0];

  // applicationID may be set if resume journey was triggered externally
  const applicationID: string | undefined = data.data?.applicationID;
  const prodCode: string | undefined = data.data?.prodCode;
  const resume = applicationID !== undefined;

  const callbackUrl = await getVersionedCallbackUrl(data.mapping);
  const userData = await getUserDetails(mobileNo);
  const journeyType = productName === "BLSE" ? "Fresh" : "Balance Transfer";

  const currentOffer: IOffers | null = data.data?.blseOffer ?? null;
  const offerProduct = currentOffer?.offerProduct ?? "";
  const offerAmount = Number(currentOffer?.icorsAmount ?? 0);
  const loanAmount = Number(data.data?.blseInsert?.loanAmt ?? 0);

  const journeyName = getJourneyName(offerProduct, loanAmount, offerAmount);
  let blseResData: { id: string } | undefined;

  if (!applicationID) {
    blseResData = await blseInsert({
      mobileNo,
      journeyName: getOrDefaultValue(journeyName, "BLSE_NTB"),
      custType: getOrDefaultValue(userData.data?.custType),
      offerId: getOrDefaultValue(currentOffer?.offerId),
      offerName: getOrDefaultValue(currentOffer?.offerName),
      loanAmt: getOrDefaultValue(data.data?.blseInsert?.loanAmt),
      offerAmt: getOrDefaultValue(currentOffer?.icorsAmount),
      tenureInMon: getOrDefaultValue(data.data?.blseInsert?.tenureInMon),
      loanVariant: getOrDefaultValue(data.data?.blseInsert?.loanVariant),
      initialTenor: "",
      subsequentTenor: "",
      roi: getOrDefaultValue(data.data?.blseInsert?.roi),
      hybridEmiAmt: "",
      droplineEmiAmt: "",
      emiAmt: getOrDefaultValue(data.data?.blseInsert?.emiAmt),
      actualEmiAmt: getOrDefaultValue(data.data?.blseInsert?.actualEmiAmt),
      maxLoanVariant: "",
      maxLoanTenor: "",
      addOnProd: [],
      appId: "",
      journeyType,
      offerProduct: getOrDefaultValue(offerProduct),
      prodName: BLSE_PROD_NAME,
      appVersion: V5,
      cartCtaStatus: getOrDefaultValue(data.data?.blseInsert?.cartCtaStatus),
      cartLanguagePref: "null",
    });
  }

  if (isOpenInApp()) {
    redirectionInAppWebform(blseResData?.id ?? (applicationID as string), productName);
    return;
  }

  redirectToWebform(
    callbackUrl.toString(),
    setFormData({
      resume,
      custType,
      ...(!applicationID ? { blseResData } : { applicationID, prodCode }),
      product,
    })
  );
};
